import { useState } from "react";
import { Ticket, CalendarPlus, ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const d = (iso) => new Date(`${iso}T12:00:00`);
export const fmtLongDate = (iso) => d(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
export const fmtShowDate = (iso, time) => `${d(iso).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}${time ? `, ore ${time}` : ", ora da definire"}`;
export const RECITAL_L = { mid: "Saggio di metà anno", final: "Saggio di fine anno" };
// spettacolo di un saggio con il titolo ancora provvisorio (non compare ad allievi e pubblico)
export const isProvisional = (ev, title = ev.title) => !!ev.linked_course && (title || "").trim() === `${RECITAL_L[ev.linked_kind]} · ${ev.linked_course}`;
const KIND_SHORT = { mid: "metà anno", final: "fine anno" };
export const LinkedTag = ({ ev, testid }) => (
  <span className="inline-flex w-fit items-center rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground break-words" data-testid={testid}>
    Saggio · {ev.linked_course} · {KIND_SHORT[ev.linked_kind]}
  </span>
);
export const isHttps = (v) => { try { const u = new URL(v); return u.protocol === "https:" && !!u.host && !/\s/.test(v); } catch { return false; } };
export const isPosterUrl = (v) => { try { return isHttps(v) && /\.(jpe?g|png|webp)$/i.test(new URL(v).pathname); } catch { return false; } };

const BTN = "inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg px-5 min-h-12 text-base font-semibold transition-colors";

// Link esterno sicuro: nuova scheda, nessun referrer, nessun dato dell'allievo
export const TicketButton = ({ href, testid }) => (isHttps(href) ? (
  <a href={href} target="_blank" rel="noopener noreferrer nofollow" referrerPolicy="no-referrer" data-testid={testid}
    className={`${BTN} bg-primary text-primary-foreground hover:bg-primary/90`}>
    <Ticket size={18} /> Compra i biglietti
  </a>
) : null);

const p2 = (n) => String(n).padStart(2, "0");
// aritmetica su data/ora locali (UTC usato solo come contatore, senza fusi)
const shift = (iso, t, minutes) => {
  const [y, mo, dd] = iso.split("-").map(Number);
  const [h, mi] = (t || "00:00").split(":").map(Number);
  const x = new Date(Date.UTC(y, mo - 1, dd, h, mi) + minutes * 60000);
  return `${x.getUTCFullYear()}${p2(x.getUTCMonth() + 1)}${p2(x.getUTCDate())}${t ? `T${p2(x.getUTCHours())}${p2(x.getUTCMinutes())}00` : ""}`;
};
export const googleCalUrl = ({ title, date, time, location, details }) => {
  const dates = time ? `${shift(date, time, 0)}/${shift(date, time, 120)}` : `${shift(date, null, 0)}/${shift(date, null, 1440)}`;
  const q = new URLSearchParams({ action: "TEMPLATE", text: title, dates, ctz: "Europe/Rome", ...(location ? { location } : {}), ...(details ? { details } : {}) });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
};

// "Aggiungi al calendario" (file .ics: iPhone/Android) + Google Calendar
export const CalendarButtons = ({ ics, testid, ...ev }) => (
  <div className="flex flex-col sm:flex-row gap-2">
    {ics && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ics/${ics}.ics`} rel="noopener" data-testid={`${testid}-ics`}
      className={`${BTN} border border-border bg-white text-foreground hover:bg-muted`}><CalendarPlus size={18} /> Aggiungi al calendario</a>}
    <a href={googleCalUrl(ev)} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" data-testid={`${testid}-gcal`}
      className={`${BTN} border border-border bg-white text-foreground hover:bg-muted`}><CalendarPlus size={18} /> Google Calendar</a>
  </div>
);

// Locandina: se non si carica, riquadro neutro con il titolo (nessuna icona rotta)
export const Poster = ({ url, title, testid, maxH = "max-h-[70vh]" }) => {
  const [bad, setBad] = useState(false);
  const [big, setBig] = useState(false);
  if (!url) return null;
  if (bad) return (
    <div className="w-full rounded-lg bg-muted flex items-center justify-center gap-2 p-6 text-center text-muted-foreground min-h-32" data-testid={`${testid}-fallback`}>
      <ImageOff size={18} className="shrink-0" /><span className="font-semibold break-words">{title}</span>
    </div>
  );
  return (
    <>
      <button type="button" onClick={() => setBig(true)} className="block w-full overflow-hidden rounded-lg bg-muted" data-testid={testid} aria-label={`Locandina: ${title}`}>
        <img src={url} alt={`Locandina: ${title}`} loading="lazy" referrerPolicy="no-referrer" onError={() => setBad(true)} className={`w-full h-auto ${maxH} object-contain`} />
      </button>
      <Dialog open={big} onOpenChange={setBig}>
        <DialogContent className="w-[calc(100%-1rem)] max-w-3xl p-2 rounded-lg" data-testid={`${testid}-big`}>
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">Locandina ingrandita</DialogDescription>
          <img src={url} alt={`Locandina: ${title}`} referrerPolicy="no-referrer" className="w-full h-auto max-h-[85vh] object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
};
